import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Dialog, Form, LongTextLabel, Toast } from "@sscs/ui";
import { useCurrentOrganization } from "Hooks";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import {
  useAddUsersToGroupMutation,
  useGetGroupQuery,
} from "../../services/organizations.api";
import { useGetUsersListQuery } from "../../services/users.api";

type FormData = {
  users: string[];
};

type AddExistingMembersProps = {
  groupId: string;
  isOpen: boolean;
  onClose: () => void;
};

const AddExistingMembersContent = ({
  groupId,
  onClose,
}: Omit<AddExistingMembersProps, "isOpen">): React.JSX.Element => {
  const currentOrganization = useCurrentOrganization();
  const [addToGroup] = useAddUsersToGroupMutation();
  const { data: group } = useGetGroupQuery({
    groupId: groupId,
  });
  const { data } = useGetUsersListQuery(
    currentOrganization
      ? {
          organization: currentOrganization.id,
          offset: 0,
          // TODO: autocomplete using list of users should be handled on the backend
          limit: 10000,
        }
      : skipToken,
  );
  const usersList = useMemo(
    () =>
      data?.users.filter(
        (user) =>
          !user.org_groups.some((item) => item.group_id === group?.group_id),
      ) ?? [],
    [data, group],
  );

  const schema: yup.SchemaOf<FormData> = yup.object({
    users: yup
      .array(yup.string().required())
      .min(1, "Please select at least 1 member"),
  });

  const methods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      users: [],
    },
  });

  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!group) {
      onClose();
      return;
    }

    try {
      await addToGroup({
        groupId: group.group_id,
        body: {
          users: data.users,
        },
      }).unwrap();

      Toast.show({
        title: "Members added to group",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not add members to group");
    }

    onClose();
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          Add members to {group?.name} group
        </Dialog.Header.Title>
      </Dialog.Header>
      <FormProvider {...methods}>
        <Form.Form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                <Form.Autocomplete
                  isRequired
                  name="users"
                  label="Select from existing organization members"
                  errors={errors}
                  options={usersList.map((user) => user.user_id)}
                  multiSelect={true}
                  getOptionLabel={(option) =>
                    usersList.find((user) => user.user_id === option)?.email ??
                    ""
                  }
                  renderOption={(props, option: string) => (
                    <li {...props}>
                      <LongTextLabel
                        label={
                          usersList.find((user) => user.user_id === option)
                            ?.email ?? ""
                        }
                      />
                    </li>
                  )}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Add members to group
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const AddExistingMembers = ({
  isOpen,
  ...props
}: AddExistingMembersProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <AddExistingMembersContent {...props} />
  </Dialog.Frame>
);

export default AddExistingMembers;
